import React, { useEffect, useState } from 'react';
import { MediaItem } from '../../../api/structs/media';
import { ClipStyleSchema, ClipStyleField } from '../../../api/clipstyleSchema';
import API, { ClipAPI } from '../../../api/api';
import { Button, AutoResizeTextarea } from '../../ui';
import { LipSyncModal, ConfirmModal } from '../../modals';
import MediaLibraryPickerModal from '../../modals/MediaLibraryPickerModal';
import { MediaLibraryItem } from '../../../api/media';
import { useToast } from '../../../hooks/useToast';
import { STORYBOARD_HIDDEN_METADATA_KEYS } from '../../../api/mediaConstants';
import {
  SceneGroup,
  getLipSyncStatus,
  getMediaFileUrl,
  getMediaThumbUrl,
  isFailed,
  isGenerating,
} from './MediaEditorSection.helpers';
import { RegenerateWatchMap, regenerateStatusOf } from './regenerateWatch';
import {
  REGENERATE_METADATA_KEYS,
  readRegenerateFailure,
  regenerateFailureText,
} from '../regenerateFailure';
import { formatSceneLabel } from '../sceneLabel';
import MediaThumbImage from '../MediaThumbImage';
import SceneVideoPoster from './SceneVideoPoster';
import ModelsAPI from '../../../api/models';
import { AIModel, usdToCredits } from '../../../api/structs/model';
import { useCostDisplay } from '../../../hooks/useCostDisplay';
import GenericCarouselThumbnailPreview from '../preview/GenericCarouselThumbnailPreview';
import ReviewerPanel from '../../clip-editor/ReviewerPanel';
import SceneAlternatesStrip from '../../clip-editor/SceneAlternatesStrip';
import AlternatesArrowsOverlay from '../../clip-editor/AlternatesArrowsOverlay';
import useSceneAlternates from '../../clip-editor/useSceneAlternates';
import type { EditorMedia } from '../../clip-editor/editorStore';
import { readContentVersion } from '../../../api/mediaUrls';

interface SceneDetailPanelProps {
  scene: SceneGroup;
  index: number;
  styleSchema: ClipStyleSchema;
  getMediaIndex: (item: MediaItem) => number;
  clipId: string;
  clipStyle: string;
  clipMetadata?: Record<string, any>;
  carouselImages?: MediaItem[];
  audios: MediaItem[];
  /**
   * In-flight regenerations, keyed by media id, owned by MediaEditorSection.
   * A regenerate never touches the canonical item until it lands, so without
   * this the panel has no way to tell "nothing happened yet" from "nothing
   * happened at all".
   */
  regenWatches?: RegenerateWatchMap;
  onRefresh: () => void;
  onPendingSaveChange?: (delta: number) => void;
  onDraftDirty?: () => void;
  onClose: () => void;
  /** Opens the scene-level regenerate modal (Video | Frames), owned by the section. */
  onRegenerateScene: () => void;
}

/**
 * The slide-shaped view of a media item the takes strip wants. Built inline
 * rather than through the editor store's `editorMediaFrom` so this panel — which
 * belongs to the clips view, not the editor — takes no runtime dependency on the
 * editor's store module. `EditorMedia` is imported as a type only.
 */
const asEditorMedia = (item: MediaItem, kind: EditorMedia['kind']): EditorMedia => {
  const contentVersion = readContentVersion(item.metadata);
  return {
    id: item.id,
    kind,
    fileUrl: item.file_url,
    ...(contentVersion === undefined ? {} : { contentVersion }),
  };
};

const MediaEditorSceneDetailPanel: React.FC<SceneDetailPanelProps> = ({
  scene,
  index,
  styleSchema,
  getMediaIndex,
  clipId,
  clipStyle,
  clipMetadata,
  carouselImages = [],
  audios,
  regenWatches = {},
  onRefresh,
  onPendingSaveChange,
  onDraftDirty,
  onClose,
  onRegenerateScene,
}) => {
  const toast = useToast();
  const display = useCostDisplay();
  // VIDEO-first: the video is the scene's deliverable, its still is merely the
  // opening frame. The old image-first order meant that as soon as a scene
  // acquired a regenerated frame, every scene-level control (prompt, provider
  // line, Regenerate/Replace/Delete, reviewer verdicts) silently retargeted
  // the IMAGE and the video became reachable only through a side-row link.
  const primaryItem = scene.video || scene.image || scene.allItems[0];

  // Takes state hoisted for BOTH media: each strip renders from its own
  // instance, and the ‹ › overlay on the primary media block pages the same
  // list the matching strip shows — one fetch per medium, never two surfaces
  // racing their own copies. A scene missing a medium leaves that hook inert.
  const videoAlternates = useSceneAlternates({
    clipId,
    slide: scene.video ? asEditorMedia(scene.video, 'video') : null,
    mediaIndex: scene.video ? getMediaIndex(scene.video) : -1,
    onPicked: () => onRefresh(),
  });
  const imageAlternates = useSceneAlternates({
    clipId,
    slide: scene.image ? asEditorMedia(scene.image, 'image') : null,
    mediaIndex: scene.image ? getMediaIndex(scene.image) : -1,
    onPicked: () => onRefresh(),
  });

  const [regenCostPrimary, setRegenCostPrimary] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchCost = async (item: MediaItem | undefined, setter: (n: number | null) => void) => {
      if (!item?.output_spec?.model) { setter(null); return; }
      const { model: modelId, provider, duration } = item.output_spec;
      const isVideo = item.type === 'ai_video';
      const list: AIModel[] = await (isVideo
        ? ModelsAPI.getVideoModels(provider || undefined)
        : ModelsAPI.getImageModels(provider || undefined)
      ).catch(() => []);
      const model = list.find((m) => m.id === modelId);
      if (!model?.pricing) { setter(null); return; }
      let credits: number | null = null;
      if (model.pricing.per_image) {
        credits = usdToCredits(parseFloat(model.pricing.per_image));
      } else if (model.pricing.per_second) {
        credits = usdToCredits(parseFloat(model.pricing.per_second) * (duration ?? 5));
      }
      if (!cancelled) setter(credits);
    };
    // One fetch is enough: whenever the scene has a video, primaryItem IS the
    // video, so this doubles as the Lip Sync glyph's cost.
    void fetchCost(primaryItem, setRegenCostPrimary);
    return () => { cancelled = true; };
  }, [primaryItem?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [prompt, setPrompt] = useState(primaryItem?.prompt || '');
  const [promptExpanded, setPromptExpanded] = useState(() => clipStyle !== 'genericCarousel');
  const fromPipes = (val: string) => val.replace(/\|/g, '\n');
  const toPipes = (val: string) => val.replace(/\n/g, '|');

  const [fieldValues, setFieldValues] = useState<Record<string, string>>(() => {
    const result: Record<string, string> = {};
    Object.entries(primaryItem?.metadata || {}).forEach(([k, v]) => {
      result[k] = fromPipes(String(v ?? ''));
    });
    return result;
  });
  const [hasSubtitle, setHasSubtitle] = useState(() =>
    primaryItem?.metadata?.subtitle_enabled !== undefined
      ? Boolean(primaryItem.metadata.subtitle_enabled)
      : Boolean(primaryItem?.metadata?.text)
  );
  const [sceneText, setSceneText] = useState(() => fromPipes(String(primaryItem?.metadata?.text || '')));
  const [replaceTarget, setReplaceTarget] = useState<MediaItem | null>(null);
  const [lipSyncVideo, setLipSyncVideo] = useState<MediaItem | null>(null);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState<MediaItem | null>(null);

  // Sync prompt and subtitle when the primary item changes (e.g., after swap + refresh)
  useEffect(() => {
    if (!primaryItem) return;
    setPrompt(primaryItem.prompt || '');
    setHasSubtitle(
      primaryItem.metadata?.subtitle_enabled !== undefined
        ? Boolean(primaryItem.metadata.subtitle_enabled)
        : Boolean(primaryItem.metadata?.text)
    );
    setSceneText(fromPipes(String(primaryItem.metadata?.text || '')));
    setFieldValues(() => {
      const result: Record<string, string> = {};
      Object.entries(primaryItem.metadata || {}).forEach(([k, v]) => {
        result[k] = fromPipes(String(v ?? ''));
      });
      return result;
    });
  }, [primaryItem?.id, primaryItem]);

  if (!primaryItem) return null;

  const isGenericCarousel = clipStyle === 'genericCarousel';
  const carouselHeadline = String(clipMetadata?.headline || '').replace(/\|/g, '\n');
  const carouselBrandingTag = String(clipMetadata?.brandingTag || 'weirdphysics');

  const withPendingSave = async (task: () => Promise<void>) => {
    onPendingSaveChange?.(1);
    try {
      await task();
    } finally {
      onPendingSaveChange?.(-1);
    }
  };

  const markRenderDirty = (key: string) => {
    if (['title', 'explanation', 'text', 'subtitle_enabled', 'order'].includes(key)) {
      onDraftDirty?.();
    }
  };

  const handlePromptBlur = async () => {
    if (prompt === primaryItem.prompt) return;
    try {
      await withPendingSave(async () => {
        await API.editMediaItem(primaryItem.id, { new_prompt_string: prompt, skip_regeneration: true });
      });
      onRefresh();
    } catch (error: any) {
      toast({ text: `Failed to save prompt: ${error.message}`, level: 'error' });
    }
  };

  const handleSubtitleToggle = async (checked: boolean) => {
    setHasSubtitle(checked);
    markRenderDirty('subtitle_enabled');
    try {
      await withPendingSave(async () => {
        await API.editMediaMetadata(primaryItem.id, 'subtitle_enabled', checked);
      });
      onRefresh();
    } catch (error: any) {
      toast({ text: `Failed to update subtitle: ${error.message}`, level: 'error' });
    }
  };

  const handleTextBlur = async () => {
    const pipedText = toPipes(sceneText);
    if (pipedText === String(primaryItem.metadata?.text || '')) return;
    markRenderDirty('text');
    try {
      await withPendingSave(async () => {
        await API.editMediaMetadata(primaryItem.id, 'text', pipedText);
      });
      onRefresh();
    } catch (error: any) {
      toast({ text: `Failed to save subtitle: ${error.message}`, level: 'error' });
    }
  };

  const handleFieldBlur = async (fieldKey: string, value: string) => {
    const pipedValue = toPipes(value);
    const original = String(primaryItem.metadata?.[fieldKey] ?? '');
    if (pipedValue === original) return;
    markRenderDirty(fieldKey);
    try {
      await withPendingSave(async () => {
        await API.editMediaMetadata(primaryItem.id, fieldKey, pipedValue);
      });
      onRefresh();
    } catch (error: any) {
      toast({ text: `Failed to save ${fieldKey}: ${error.message}`, level: 'error' });
    }
  };

  const handleReplace = async (item: MediaItem, selected: MediaLibraryItem) => {
    const kind: 'image' | 'ai_video' | 'audio' =
      item.type === 'ai_video' ? 'ai_video' : item.type === 'audio' ? 'audio' : 'image';
    try {
      await ClipAPI.swapClipMedia(clipId, {
        kind,
        media_index: Math.max(0, getMediaIndex(item)),
        new_media_id: selected.id,
        // The slot named by the id it holds — the index is a legacy fallback
        // computed from a delivered order the store never guaranteed.
        old_media_id: item.id,
      });
      toast({ text: `Replaced with ${selected.name || selected.id}`, level: 'success' });
      onRefresh();
    } catch (error: any) {
      toast({ text: `Failed to replace: ${error.message}`, level: 'error' });
    }
  };

  const handleDelete = async (item: MediaItem) => {
    try {
      await API.deleteMediaItem(item.id);
      onRefresh();
    } catch (error: any) {
      toast({ text: `Failed to delete: ${error.message}`, level: 'error' });
    }
  };

  const handleUndoTrim = async (beforeDuration?: number) => {
    try {
      await withPendingSave(async () => {
        await API.editMediaMetadata(primaryItem.id, 'trim_start', null);
        await API.editMediaMetadata(primaryItem.id, 'trim_end', null);
        // A declared duration beats the trim range everywhere it is read, so
        // restore it to the pre-trim length — or clear it outright when no
        // before-value could be derived — rather than leave the item at the
        // trimmed length.
        await API.editMediaMetadata(primaryItem.id, 'duration', beforeDuration ?? null);
      });
      onRefresh();
    } catch (error: any) {
      toast({ text: `Couldn't undo the trim: ${error.message}`, level: 'error' });
    }
  };

  const visibleMetadata = Object.entries(primaryItem.metadata || {})
    // The regenerate-failure keys have a dedicated line below; without this they
    // would ALSO land in the raw-metadata fallback as editable textareas, where
    // "fixing" the error text would just overwrite the backend's next stamp.
    // (They belong in STORYBOARD_HIDDEN_METADATA_KEYS proper — that list lives
    // in api/mediaConstants.ts, outside this surface's ownership.)
    .filter(([key]) => !STORYBOARD_HIDDEN_METADATA_KEYS.has(key) && !REGENERATE_METADATA_KEYS.has(key));
  const typeFields = (styleSchema.mediaMetadataFields?.[primaryItem.type] || []) as ClipStyleField[];
  const visibleFields = typeFields.filter((f) => !STORYBOARD_HIDDEN_METADATA_KEYS.has(f.key));
  const lipSyncStatus = scene.video ? getLipSyncStatus(scene.video) : null;
  const sceneLabel = formatSceneLabel(scene.sceneId, index).toUpperCase();

  // The still is the video's opening frame, so a playable video absorbs it as
  // its poster (SceneVideoPoster) — the standalone image block survives only
  // while there is nothing playable to poster.
  const imageUrl = getMediaFileUrl(scene.image);
  // `_waiting` and `_failed` are NOT the same state and used to be tested by
  // one predicate: a failed item spun the "generating…" spinner below forever,
  // which is the single most misleading thing this panel could say about it.
  const videoWaiting = !!scene.video?.file_url && isGenerating(scene.video.file_url);
  const videoFailed = !!scene.video?.file_url && isFailed(scene.video.file_url);
  const imageWaiting = !!scene.image?.file_url && isGenerating(scene.image.file_url);
  const imageFailed = !!scene.image?.file_url && isFailed(scene.image.file_url);
  const hasPlayableVideo = !!scene.video?.file_url && !videoWaiting && !videoFailed;
  const showStandaloneImage =
    !!scene.image && !!imageUrl && !imageWaiting && !imageFailed && !hasPlayableVideo;
  // A pending video shows the still (above) plus the spinner; an image-only
  // scene falls back to its own placeholder state.
  const showSpinner = videoWaiting || (!scene.video && imageWaiting);
  const showFailedPlaceholder = videoFailed || (!scene.video && imageFailed);

  // A REGENERATE is a different thing from the two placeholders above: it never
  // touches the canonical item's bytes, so it has no placeholder of its own and
  // its whole visible life is these two lines.
  const primaryRegenStatus = regenerateStatusOf(regenWatches, primaryItem.id);
  const primaryRegenFailure = readRegenerateFailure(primaryItem);
  const frameItem = scene.image && scene.image.id !== primaryItem.id ? scene.image : null;
  const frameRegenStatus = regenerateStatusOf(regenWatches, frameItem?.id);
  const frameRegenFailure = readRegenerateFailure(frameItem);

  return (
    <>
      <div className="mt-2 border border-white/10 bg-black p-4 font-mono">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-400">{sceneLabel}</p>
          <button onClick={onClose} className="text-zinc-600 hover:text-zinc-300 text-sm transition-colors">✕</button>
        </div>

        <ReviewerPanel
          clipId={clipId}
          mediaId={primaryItem.id}
          metadata={primaryItem.metadata || {}}
          onUndoTrim={handleUndoTrim}
          onRefresh={onRefresh}
        />

        {/* One strip per medium, each bound to ITS OWN item. `primaryItem`
            favours one medium (the video, nowadays) — binding a single strip
            to it would offer that medium's takes under the other and swap the
            wrong slot. The editor's own panel resolves by the clicked slide and
            never had the problem; this one shows both media at once, so it
            gets two strips. Each hides itself when its item has no history. */}
        {scene.image && (
          <SceneAlternatesStrip
            key={`alternates-image-${scene.image.id}`}
            clipId={clipId}
            slide={asEditorMedia(scene.image, 'image')}
            mediaIndex={getMediaIndex(scene.image)}
            onPicked={() => onRefresh()}
            state={imageAlternates}
          />
        )}
        {scene.video && (
          <SceneAlternatesStrip
            key={`alternates-video-${scene.video.id}`}
            clipId={clipId}
            slide={asEditorMedia(scene.video, 'video')}
            mediaIndex={getMediaIndex(scene.video)}
            onPicked={() => onRefresh()}
            state={videoAlternates}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-2">
            {isGenericCarousel && scene.image ? (
              <GenericCarouselThumbnailPreview
                images={carouselImages}
                activeItemId={scene.image.id}
                sceneIndex={index}
                headline={carouselHeadline}
                brandingTag={carouselBrandingTag}
                draftMetadata={{ ...(clipMetadata || {}), ...fieldValues }}
              />
            ) : (
              <>
                {showStandaloneImage && (
                  <div data-testid="scene-image-block" className="relative">
                    <MediaThumbImage
                      thumbUrl={getMediaThumbUrl(scene.image)}
                      originalUrl={imageUrl}
                      preferOriginal
                      alt={scene.image!.prompt}
                      className="w-full h-32 rounded border border-white/5"
                      imgClassName="w-full rounded object-cover border border-white/5"
                    />
                    {/* Image takes only for a scene with no video at all: with
                        one merely pending, arrows here would rebind the still
                        under a spinner that is about to replace the picture. */}
                    {!scene.video && (
                      <AlternatesArrowsOverlay state={imageAlternates} label="frame take" />
                    )}
                  </div>
                )}
                {showSpinner && (
                  <div className="w-full h-32 bg-zinc-900 flex flex-col gap-2 items-center justify-center text-zinc-600 text-xs">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 animate-spin text-zinc-500">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                    <span>generating...</span>
                  </div>
                )}
                {showFailedPlaceholder && (
                  <div
                    data-testid="scene-media-failed"
                    className="w-full h-32 bg-zinc-900 flex flex-col gap-1 items-center justify-center text-red-400/80 text-xs"
                  >
                    <span className="text-lg leading-none">⚠</span>
                    <span>generation failed</span>
                  </div>
                )}
                {hasPlayableVideo && (
                  <SceneVideoPoster
                    video={scene.video!}
                    posterImage={scene.image}
                    // The poster is the VIDEO, so its arrows page the video's
                    // takes — the still's live on its own block/strip. Picking
                    // rebinds file_url (or the id), and the poster re-keys off
                    // the refreshed scene, so the frame under the arrows is
                    // always the take just chosen.
                    overlay={<AlternatesArrowsOverlay state={videoAlternates} label="video take" />}
                  />
                )}
              </>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <button
                onClick={() => setPromptExpanded((v) => !v)}
                className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 transition-colors mb-1"
              >
                <span>{promptExpanded ? '−' : '+'}</span>
                <span>Prompt</span>
              </button>
              {promptExpanded && (
                <AutoResizeTextarea
                  className="w-full bg-zinc-900 border border-white/10 text-sm text-white px-3 py-2 resize-none focus:outline-none focus:border-white/25 font-sans"
                  rows={2}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onBlur={handlePromptBlur}
                  placeholder="Prompt..."
                />
              )}
            </div>

            {!isGenericCarousel && (
            <div className="border border-white/10 p-3">
              <div className="flex items-center gap-2 mb-2">
                <input
                  id={`subtitle-${scene.sceneId}`}
                  type="checkbox"
                  checked={hasSubtitle}
                  onChange={(e) => handleSubtitleToggle(e.target.checked)}
                  className="h-3.5 w-3.5 rounded-none border-zinc-600 bg-zinc-900 accent-white cursor-pointer"
                />
                <label
                  htmlFor={`subtitle-${scene.sceneId}`}
                  className="text-[10px] uppercase tracking-widest text-zinc-500 cursor-pointer"
                >
                  Scene Subtitle
                </label>
              </div>
              {hasSubtitle && (
                <AutoResizeTextarea
                  className="w-full bg-zinc-900 border border-white/10 text-sm text-white px-3 py-2 resize-none focus:outline-none focus:border-white/25 font-sans mt-2"
                  rows={2}
                  value={sceneText}
                  onChange={(e) => {
                    markRenderDirty('text');
                    setSceneText(e.target.value);
                  }}
                  onBlur={handleTextBlur}
                  placeholder="Subtitle text shown on screen..."
                />
              )}
            </div>
            )}

            {visibleFields.length > 0 && (
              <div className="space-y-3">
                {visibleFields.slice(0, 4).map((f) => (
                  <div key={f.key}>
                    <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-0.5">
                      {f.label}
                    </label>
                    {f.type === 'textarea' ? (
                      <AutoResizeTextarea
                        className="w-full bg-transparent text-sm text-zinc-300 leading-relaxed outline-none border-b border-transparent hover:border-zinc-700 focus:border-white/30 transition-colors resize-none font-sans py-0.5"
                        rows={1}
                        value={fieldValues[f.key] ?? ''}
                        onChange={(e) => {
                          markRenderDirty(f.key);
                          setFieldValues((prev) => ({ ...prev, [f.key]: e.target.value }));
                        }}
                        onBlur={(e) => handleFieldBlur(f.key, e.target.value)}
                      />
                    ) : (
                      <AutoResizeTextarea
                        className="w-full bg-transparent text-sm text-zinc-300 outline-none border-b border-transparent hover:border-zinc-700 focus:border-white/30 transition-colors resize-none font-sans py-0.5"
                        rows={1}
                        value={fieldValues[f.key] ?? ''}
                        onChange={(e) => {
                          markRenderDirty(f.key);
                          setFieldValues((prev) => ({ ...prev, [f.key]: e.target.value }));
                        }}
                        onBlur={(e) => handleFieldBlur(f.key, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
            {visibleFields.length === 0 && visibleMetadata.length > 0 && (
              <div className="space-y-3">
                {visibleMetadata.slice(0, 4).map(([key]) => (
                  <div key={key}>
                    <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-0.5">
                      {key}
                    </label>
                    <AutoResizeTextarea
                      className="w-full bg-transparent text-sm text-zinc-300 outline-none border-b border-transparent hover:border-zinc-700 focus:border-white/30 transition-colors resize-none font-sans py-0.5"
                      rows={1}
                      value={fieldValues[key] ?? ''}
                      onChange={(e) => {
                        markRenderDirty(key);
                        setFieldValues((prev) => ({ ...prev, [key]: e.target.value }));
                      }}
                      onBlur={(e) => handleFieldBlur(key, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            )}

            {lipSyncStatus && (
              <p className="text-[10px] text-zinc-600">
                lip sync:{' '}
                {lipSyncStatus === 'done' ? (
                  <span className="text-white">done ✓</span>
                ) : (
                  <span className="text-zinc-400">pending</span>
                )}
              </p>
            )}

            {primaryItem.output_spec?.provider && (
              <p className="text-[10px] text-zinc-700">
                {primaryItem.output_spec.provider} / {primaryItem.output_spec.model}
              </p>
            )}
            {/* The provider line above follows primaryItem — the video, when
                the scene has one. Keep the opening frame's provenance visible
                too, since its regenerate now lives behind the Frames tab. */}
            {scene.video && scene.image?.output_spec?.provider && (
              <p className="text-[10px] text-zinc-700">
                frame: {scene.image.output_spec.provider} / {scene.image.output_spec.model}
              </p>
            )}

            {/* Directly under the provenance line, because that is the line
                that says WHICH provider was asked — and a rejection is the
                provider answering. An in-flight attempt hides the previous
                attempt's error: it is being retried, saying otherwise would be
                stale by the time the user finished reading it. */}
            {primaryRegenStatus === 'running' && (
              <p data-testid="scene-regenerating" className="text-[10px] text-amber-300">
                Regenerating… this can take a few minutes.
              </p>
            )}
            {primaryRegenStatus === 'stalled' && (
              <p data-testid="scene-regen-stalled" className="text-[10px] text-amber-400">
                Regenerating — still no result. The attempt may have been lost; try again.
              </p>
            )}
            {!primaryRegenStatus && primaryRegenFailure && (
              <p data-testid="scene-regenerate-error" className="text-[10px] text-red-400">
                {regenerateFailureText(primaryRegenFailure)}
              </p>
            )}
            {frameRegenStatus === 'running' && (
              <p data-testid="frame-regenerating" className="text-[10px] text-amber-300">
                frame: regenerating…
              </p>
            )}
            {!frameRegenStatus && frameRegenFailure && (
              <p data-testid="frame-regenerate-error" className="text-[10px] text-red-400">
                frame: {regenerateFailureText(frameRegenFailure)}
              </p>
            )}
          </div>
        </div>

        {/* ONE row of scene-level actions. Regenerate opens the section-owned
            SceneRegenerateModal (Video | Frames) — the old side-row that was the
            only way to reach the video's regenerate is gone with it. */}
        <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-white/10">
          <Button size="sm" variant="ghost" onClick={onRegenerateScene}>
            <span className="flex items-center gap-1">
              Regenerate
              {display === 'credits' && regenCostPrimary != null && (
                <span className="credit-icon">⚡</span>
              )}
            </span>
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setReplaceTarget(primaryItem)}>Replace</Button>
          {scene.video && (
            <Button size="sm" variant="ghost" onClick={() => setLipSyncVideo(scene.video!)}>
              <span className="flex items-center gap-1">
                Lip Sync
                {display === 'credits' && regenCostPrimary != null && <span className="credit-icon">⚡</span>}
              </span>
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteItem(primaryItem)}>Delete</Button>
        </div>

      </div>

      {replaceTarget && (
        <MediaLibraryPickerModal
          isOpen={!!replaceTarget}
          onClose={() => setReplaceTarget(null)}
          filterType={replaceTarget.type === 'ai_video' ? 'video' : replaceTarget.type === 'audio' ? 'audio' : 'image'}
          onSelect={(selected) => { handleReplace(replaceTarget, selected); setReplaceTarget(null); }}
        />
      )}

      {lipSyncVideo && (
        <LipSyncModal
          isOpen={!!lipSyncVideo}
          onClose={() => setLipSyncVideo(null)}
          clipId={clipId}
          video={lipSyncVideo}
          audios={audios}
          onSuccess={() => { setLipSyncVideo(null); onRefresh(); }}
        />
      )}

      {confirmDeleteItem && (
        <ConfirmModal
          isOpen={!!confirmDeleteItem}
          onClose={() => setConfirmDeleteItem(null)}
          onConfirm={() => {
            if (confirmDeleteItem) handleDelete(confirmDeleteItem);
            setConfirmDeleteItem(null);
          }}
          title="Delete Media"
          message={`Delete this ${confirmDeleteItem.type} item? This cannot be undone.`}
          confirmText="Delete"
          variant="danger"
        />
      )}
    </>
  );
};

export default MediaEditorSceneDetailPanel;
